import React, { useEffect, useState } from 'react';
import { MultiSelect } from '../../other/MultiSelect';
// import {Tooltip} from "react-tooltip";
import { getFacilitiesByCompanySettingsInit } from '../../../services/facility';
import { getUserGroupsMembersSettingsInit, getUsersListSettingsInit } from '../../../services/user';
import { getCompanyUserGroupListSettingsInit } from '../../../services/company';
import { useAuth } from '../../../container/authContext';
import { usePermission } from '../../../container/permissionContext';
import { useParams } from 'react-router-dom';
import StrictOrderSteps from '../../other/strict-order-steps';
import {
  canInitiatorFilterByHireDate,
  createEmptyStep,
  createFixedFinalStep,
  createInitialSteps,
  createLockedStep,
  getStepName,
  getStepNotification,
  getStepSubmitterOptions,
  getStepSubmitters,
  hasCreatorSteps,
  hasMultipleSubmissionsCreatorSteps,
  isFixedFinalStep,
  isLockedStep,
  isUserGroupSubmitter,
  toFixedFinalOption,
  toStepSubmitters,
} from '../../../utility/forms/strict-order';

export const SubmissionSettingsInitSystem = function SubmissionSettingsInitSystem({ formData, handelInitSettings, initData, errors }) {
  const [recipient_settings_init, setRecipientSettingsInit] = useState({});
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [usersGroupsOptions, setUsersGroupsOptions] = useState([]);
  const [individualUsersOptions, setIndividualUsersOptions] = useState([]);
  // The members of user groups as options, with the groups they were fetched for (see withMemberHireDates).
  const [groupMembers, setGroupMembers] = useState({ groupIds: [], options: [] });
  const { user } = useAuth();
  const { facility: userFacility } = user ?? {};
  const companyId = user.company?.id;
  const { id } = useParams();
  const { isSuperAdmin, isCompanySystemAdministrator } = usePermission();

  // "Submitters only from the initiator's facility": an employee's is their own, an admin
  // has none, so they pick the facilities first and choose the submitters from those alone.
  const isFromInitiatorFacility = !!formData?.submission_settings?.submitters?.is_users_from_initiator_facility;
  const shouldPickFacility = isFromInitiatorFacility && (isSuperAdmin() || isCompanySystemAdministrator());
  // A string, so the user groups are fetched again when the picked facilities change, not on every render.
  const pickedFacilityIdsKey = (recipient_settings_init.facilities ?? []).map((item) => item.value).join(',');

  // On such a form whose steps the creator set up, an employee does not pick the submitters:
  // the API sends the creator's of their facility, which are shown locked and fill the empty steps.
  const lockedSubmitters = formData?.initiator_locked_submitters ?? null;

  // "Specify submissions number" = Multiple: one submission per step-1 user, so a
  // step can name several submitters instead of one.
  const hasMultipleSubmissionsStrictOrder = formData?.submission_settings?.only_one_submission === false;

  // A user the API locks as the last step (another company's, so never in the
  // pickers): the initiator chooses step 1 as usual and step 2 is this user alone.
  const fixedFinalSubmitter = formData?.submission_settings?.has_strict_order_of_submissions ? formData?.fixed_final_submitter : null;

  const lockedFinalSubmitters = fixedFinalSubmitter ? [toFixedFinalOption(fixedFinalSubmitter)] : null;

  // Steps the form creator set up (CREATOR_STEPS_KEY): their number, names and notifications
  // are fixed, a step given submitters is locked to them and not shown, an empty one is the
  // initiator's to fill.
  const creatorSteps =
    !lockedFinalSubmitters && hasCreatorSteps(formData?.submission_settings) ? formData.submission_settings.submitters_order ?? [] : null;
  // With Multiple, how the steps are notified is the creator's alone: the initiator does not see it.
  const hideStepNotifications = !!creatorSteps && hasMultipleSubmissionsCreatorSteps(formData?.submission_settings);
  const creatorSubmitterOptions = [
    // Every user the creator's steps name - a member of one of the user groups below too.
    ...(formData?.step_users ?? []).map((item) => ({
      value: item.id,
      label: `${item.last_name} ${item.first_name}`,
      entity_type: 'user',
    })),
    ...(formData?.submission_settings?.submitters?.individual_users ?? []).map((item) => ({
      value: item.id,
      label: `${item.last_name} ${item.first_name}`,
      entity_type: 'user',
    })),
    ...(formData?.submission_settings?.submitters?.user_groups ?? []).map((item) => ({
      value: item.id,
      label: item.name,
      entity_type: 'user_group',
    })),
  ];
  const creatorLockedSubmitters = (creatorSteps ?? []).map((step) =>
    getStepSubmitters(step).length ? getStepSubmitterOptions(step, creatorSubmitterOptions) : null,
  );
  // With every step filled by the creator there is no step left to show.
  const hasStepsToFill = !creatorLockedSubmitters.length || creatorLockedSubmitters.some((submitters) => !submitters);

  // A step filtered by hire date offers a user group only with a member hired in its month
  // (isOfHireMonth): the hire dates of its members, once they are fetched.
  const withMemberHireDates = (option) => {
    const key = String(option.value);
    if (!isUserGroupSubmitter(option) || !groupMembers.groupIds.includes(key)) {
      return option;
    }

    const members = groupMembers.options.filter((member) => member.user_group_keys.includes(key));

    return { ...option, member_hire_dates: members.map((member) => member.hire_date) };
  };

  // A step may also name one member of the user groups picked, as in the form builder; "Individual
  // users" leaves them out, so they are fetched on their own and each step's "Individual users"
  // picker offers them after the picked users. Strings, so they are fetched when the picked groups
  // or facilities change, not on every render.
  const hasStrictOrder = !!formData?.submission_settings?.has_strict_order_of_submissions;
  const pickedUserGroupIdsKey = (recipient_settings_init.user_groups ?? []).map((item) => item.value).join(',');
  const memberFacilityIdsKey = isFromInitiatorFacility && !shouldPickFacility ? `${userFacility?.id ?? ''}` : pickedFacilityIdsKey;
  const pickedUserValues = (recipient_settings_init.individual_users ?? []).map((item) => String(item.value));

  const stepSubmitterOptions = [
    ...(recipient_settings_init.individual_users ?? []),
    ...groupMembers.options.filter((option) => !pickedUserValues.includes(String(option.value))),
    ...(recipient_settings_init.user_groups ?? []).map(withMemberHireDates),
  ];

  const getStepErrors = (stepIndex) => errors?.submitters_settings?.strict_order?.[stepIndex];

  const renderStepErrors = (stepIndex) => (
    <>
      <span className="form-error mb-2">
        {errors?.submitters_settings?.strict_order &&
          errors?.submitters_settings?.strict_order[stepIndex]?.hasOwnProperty('submitter') &&
          errors?.submitters_settings?.strict_order[stepIndex].submitter}
      </span>
      <span className="form-error mb-2">
        {errors?.submitters_settings?.strict_order &&
          typeof errors?.submitters_settings?.strict_order[stepIndex] === 'string' &&
          errors?.submitters_settings?.strict_order[stepIndex]}
      </span>
    </>
  );

  useEffect(() => {
    if (shouldPickFacility || lockedSubmitters) {
      return;
    }

    const { submission_settings } = formData ?? {};
    const isNeedGetCompanyUserGroup =
      (!!submission_settings?.can_initiator_select_submitters && submission_settings?.submitters?.is_users_from_initiator_facility) ||
      (!submission_settings?.submitters?.is_users_from_initiator_facility && recipient_settings_init?.facilities?.length);
    if (isNeedGetCompanyUserGroup) {
      getCompanyUserGroup(userFacility ? [userFacility.id] : []);
    }
  }, [formData, recipient_settings_init?.facilities?.length, userFacility, shouldPickFacility, lockedSubmitters]);

  useEffect(() => {
    if (shouldPickFacility && pickedFacilityIdsKey) {
      getCompanyUserGroup(pickedFacilityIdsKey.split(',').map(Number));
    }
    // eslint-disable-next-line
  }, [shouldPickFacility, pickedFacilityIdsKey]);

  useEffect(() => {
    if (!shouldPickFacility) {
      return;
    }

    getFacilitiesByCompanySettingsInit(companyId, 'getAll', id, 'submitters_settings')
      .then((res) => setFacilityOptions(res.map((item) => ({ value: item.id, label: item.name }))))
      .catch((e) => console.log(e));
  }, [shouldPickFacility, companyId, id]);

  useEffect(() => {
    if (formData?.submission_settings?.submitters) {
      const prevState = formData.submission_settings.submitters;
      if (prevState.facilities?.length) {
        setFacilityOptions(prevState.facilities.map((item) => ({ value: item.id, label: item.name })));
      }
    }
    // eslint-disable-next-line
  }, [formData, recipient_settings_init]);

  useEffect(() => {
    if (!isFromInitiatorFacility || shouldPickFacility) {
      if (!recipient_settings_init?.facilities?.length) {
        const prevState = { ...recipient_settings_init };
        prevState['user_groups'] = [];
        prevState['individual_users'] = [];
        setRecipientSettingsInit(prevState);
        handelInitSettings(prevState, 'submitters_settings');
        setIndividualUsersOptions([]);
        setUsersGroupsOptions([]);
      }
    }
    // eslint-disable-next-line
  }, [recipient_settings_init.facilities]);

  useEffect(() => {
    if (lockedSubmitters) {
      return;
    }

    if (shouldPickFacility) {
      // Nobody to choose from before a facility is picked.
      if (recipient_settings_init.facilities?.length) {
        getUsersList(recipient_settings_init.facilities.map((item) => item.value));
      }
    } else if (!formData?.recipient_settings?.recipients_only_from_initiator_facility) {
      getUsersList(recipient_settings_init?.facilities ? [...recipient_settings_init.facilities.map((item) => item.value)] : []);
    } else {
      getUsersList(user.facility ? [user.facility.id] : []);
    }
  }, [
    formData?.recipient_settings?.recipients_only_from_initiator_facility,
    recipient_settings_init.facilities,
    recipient_settings_init.user_groups,
    user.facility,
    shouldPickFacility,
    lockedSubmitters,
  ]);

  const getCompanyUserGroup = (facilities_ids = []) => {
    getCompanyUserGroupListSettingsInit({
      company_id: companyId,
      status: 'active',
      form_id: id,
      field: 'submitters_settings',
      facilities_ids: facilities_ids,
    })
      .then((res) => {
        const options = res.map((item) => ({ value: item.id, label: item.name, entity_type: 'user_group' }));
        setUsersGroupsOptions(options);
        if (options) {
          const newState = [];
          initData?.submitters_settings?.user_groups?.forEach((item) => {
            if (options.find((option) => option.value === item.value)) {
              newState.push(item);
            }
          });
          // Merge into the live state: this lands after the request, by which time
          // the state captured when the request was fired is stale and replacing it
          // wholesale would drop everything else (submitters_order included).
          setRecipientSettingsInit((prevState) => ({ ...prevState, user_groups: newState }));
        }
      })
      .catch((e) => console.log(e));
  };

  // The members of the user groups `groupIdsKey` names, as user options with their groups (`user_group_keys`).
  const fetchGroupMembers = (groupIdsKey) => {
    const groupIds = groupIdsKey.split(',');

    return getUserGroupsMembersSettingsInit({
      company_id: companyId,
      status: 'active',
      form_id: id,
      field: 'submitters_settings',
      facilities_ids: memberFacilityIdsKey ? memberFacilityIdsKey.split(',').map(Number) : [],
      user_groups_ids: groupIds,
    }).then((res) => ({
      groupIds,
      options: res.map((item) => ({
        value: item.id,
        label: `${item.last_name} ${item.first_name}`,
        entity_type: 'user',
        hire_date: item.hire_date,
        user_group_keys: item.user_group_keys ?? [],
      })),
    }));
  };

  const getUsersList = (facilities_ids = []) => {
    getUsersListSettingsInit({
      company_id: companyId,
      status: 'active',
      form_id: id,
      field: 'submitters_settings',
      facilities_ids: facilities_ids,
      user_groups_ids: recipient_settings_init?.user_groups ? [...recipient_settings_init.user_groups.map((item) => item.value)] : [],
    })
      .then((resUsers) => {
        const options = resUsers.map((item) => ({
          value: item.id,
          label: item.last_name + ' ' + item.first_name,
          entity_type: 'user',
          hire_date: item.hire_date,
        }));
        setIndividualUsersOptions(options);
        if (options) {
          const newState = [];
          initData?.submitters_settings?.individual_users?.forEach((item) => {
            if (options.find((option) => option.value === item.value)) {
              newState.push(item);
            }
          });
          // See getCompanyUserGroup: merge rather than replace, the captured state is stale.
          setRecipientSettingsInit((prevState) => ({ ...prevState, individual_users: newState }));
        }
      })
      .catch((e) => console.log(e));
  };

  useEffect(() => {
    if (!hasStrictOrder || !pickedUserGroupIdsKey) {
      setGroupMembers({ groupIds: [], options: [] });
      return;
    }

    fetchGroupMembers(pickedUserGroupIdsKey)
      .then(setGroupMembers)
      .catch((e) => console.log(e));
    // eslint-disable-next-line
  }, [hasStrictOrder, pickedUserGroupIdsKey, memberFacilityIdsKey, companyId, id]);

  useEffect(() => {
    if (initData?.submitters_settings) {
      setRecipientSettingsInit({ ...initData?.submitters_settings });
    }
  }, [initData]);

  useEffect(() => {
    if (!fixedFinalSubmitter) {
      return;
    }

    // Keep the order at exactly [initiator's step, fixed user]; the screen never
    // lets the second one change, this puts it there in the first place.
    const order = initData?.submitters_settings?.submitters_order ?? [];
    if (order.length === 2 && isFixedFinalStep(order[1], fixedFinalSubmitter)) {
      return;
    }

    const nextState = {
      ...(initData?.submitters_settings ?? {}),
      submitters_order: [order[0] ?? createEmptyStep(0), createFixedFinalStep(fixedFinalSubmitter, 1)],
    };
    setRecipientSettingsInit(nextState);
    handelInitSettings(nextState, 'submitters_settings');
    // eslint-disable-next-line
  }, [fixedFinalSubmitter, initData?.submitters_settings?.submitters_order]);

  useEffect(() => {
    if (!lockedSubmitters) {
      return;
    }

    // Keep the submitters at the creator's, the same way the steps are kept below.
    const settings = initData?.submitters_settings ?? {};
    const isSame = (selected, locked) => (selected ?? []).map(({ value }) => value).join() === locked.map(({ value }) => value).join();
    if (
      isSame(settings.user_groups, lockedSubmitters.user_groups) &&
      isSame(settings.individual_users, lockedSubmitters.individual_users)
    ) {
      return;
    }

    const nextState = { ...settings, user_groups: lockedSubmitters.user_groups, individual_users: lockedSubmitters.individual_users };
    setRecipientSettingsInit(nextState);
    handelInitSettings(nextState, 'submitters_settings');
    // eslint-disable-next-line
  }, [lockedSubmitters, initData?.submitters_settings]);

  useEffect(() => {
    if (!creatorSteps) {
      return;
    }

    // Keep the order at the creator's steps: as many, the filled ones as the creator set
    // them, every "Notify user" the creator's. Only what the initiator put on an empty step stays.
    const order = initData?.submitters_settings?.submitters_order ?? [];
    const isInPlace =
      order.length === creatorSteps.length &&
      creatorLockedSubmitters.every((lockedSubmitters, index) => !lockedSubmitters || isLockedStep(order[index], lockedSubmitters));
    if (isInPlace) {
      return;
    }

    const nextState = {
      ...(initData?.submitters_settings ?? {}),
      submitters_order: creatorSteps.map((creatorStep, index) => ({
        ...(creatorLockedSubmitters[index]
          ? createLockedStep(creatorLockedSubmitters[index], index)
          : order[index] ?? createEmptyStep(index)),
        ...getStepNotification(creatorStep),
        step: index,
      })),
    };
    setRecipientSettingsInit(nextState);
    handelInitSettings(nextState, 'submitters_settings');
    // eslint-disable-next-line
  }, [creatorSteps, initData?.submitters_settings?.submitters_order]);

  const handelRemove = (key, value) => {
    const prevState = { ...recipient_settings_init };
    prevState[key] = prevState[key].filter((item) => item.value !== value.value);
    setRecipientSettingsInit(() => {
      return prevState;
    });

    handelInitSettings(prevState, 'submitters_settings');
  };

  const handelSettings = (value, key) => {
    const prevState = { ...recipient_settings_init };
    prevState[key] = value;
    setRecipientSettingsInit(() => prevState);

    handelInitSettings(prevState, 'submitters_settings');
  };

  const addNewOrder = () => {
    const prevState = { ...recipient_settings_init };

    if (!prevState['submitters_order']) {
      // The first two steps are always on screen but only exist in state once
      // something is set on them, so materialise them before appending a third.
      prevState['submitters_order'] = createInitialSteps();
    }

    prevState['submitters_order'] = [...prevState['submitters_order'], createEmptyStep(prevState['submitters_order'].length)];

    setRecipientSettingsInit(() => {
      return prevState;
    });

    handelInitSettings(prevState, 'submitters_settings');
  };
  const orderRemove = (element, index) => {
    const prevState = { ...recipient_settings_init };
    prevState['submitters_order'] = prevState['submitters_order']
      .filter((item, itemIndex) => (element ? item !== element : itemIndex !== index))
      .map((item, itemIndex) => ({ ...item, step: itemIndex }));
    setRecipientSettingsInit(() => {
      return prevState;
    });

    handelInitSettings(prevState, 'submitters_settings');
  };

  const handelOrderSettings = (value, key, index) => {
    const prevState = { ...recipient_settings_init };
    if (!prevState[key]) {
      prevState[key] = createInitialSteps();
    }

    prevState[key] = prevState[key].map((item, itemIndex) => (itemIndex === index ? { ...(item ?? {}), ...value } : item));

    setRecipientSettingsInit(() => {
      return prevState;
    });

    handelInitSettings(prevState, 'submitters_settings');
  };

  // `stepData` is more of the step to change along with its submitters (its hire date).
  const handleChangeStepSubmitters = (selection, stepIndex, stepData = {}) => {
    if (hasMultipleSubmissionsStrictOrder) {
      handelOrderSettings({ submitters: toStepSubmitters(selection), ...stepData }, 'submitters_order', stepIndex);
      return;
    }

    const { value, label, entity_type, public: isPublic } = selection ?? {};
    handelOrderSettings({ submitter: value, label, entity_type, public: isPublic, value, ...stepData }, 'submitters_order', stepIndex);
  };

  const handleChangeStep = (newData, stepIndex) => handelOrderSettings(newData, 'submitters_order', stepIndex);

  return (
    <div className="form-builder-setting-tab-content">
      <div className="form-builder-recipient-settings">
        <p className="info-text">
          <i className="icon-icon-info"></i> Choose submitters and submission rules.
        </p>
        <span className="form-error mb-2 d-flex">{errors?.non_field_errors && errors.non_field_errors[0]}</span>

        {/* An employee given the creator's submitters (lockedSubmitters) has none to pick: they
            only fill the creator's empty steps, from those submitters. */}
        {!lockedSubmitters && (
          <div className="form-control-item-settings">
            <div className="form-settings-label">
              <label className="form-label form-label-big">Specify Submitters (required)</label>
              <span>Submitters are users to whom you are sending this form completion.</span>
            </div>
            <div className="form-settings-inputs">
              <>
                <div className={`form-item form-item-select mb-4`}>
                  <label className="form-label form-label-big">Facility</label>
                  <div className="select-multy-wrapper">
                    <span className="select-placeholder">Choose one or several facilities</span>
                    <MultiSelect
                      isDisabled={!facilityOptions.length}
                      options={facilityOptions}
                      value={recipient_settings_init.facilities}
                      onChange={(e) => handelSettings(e, 'facilities')}
                      placeholder=""
                      allName="All facilities"
                    />
                    {recipient_settings_init.facilities?.length > 0 && (
                      <>
                        <ul>
                          {recipient_settings_init.facilities.map((item, key) => (
                            <li key={key}>
                              {item.label}
                              <button className="btn btn-icon" onClick={() => handelRemove('facilities', item)}>
                                <img src="/images/x-circle.svg" alt="" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                  <span className="form-error">{errors?.submitters_settings?.facilities && errors.submitters_settings.facilities[0]}</span>
                </div>

                <div className={`form-item form-item-select mb-4`}>
                  <label className="form-label form-label-big">User groups</label>
                  <div className="select-multy-wrapper">
                    <span className="select-placeholder">Choose one or several user groups</span>
                    <MultiSelect
                      isDisabled={!usersGroupsOptions.length}
                      options={usersGroupsOptions}
                      value={recipient_settings_init.user_groups}
                      onChange={(e) => handelSettings(e, 'user_groups')}
                      placeholder=""
                      allName="All user groups"
                    />
                    {recipient_settings_init.user_groups?.length > 0 && (
                      <>
                        <ul>
                          {recipient_settings_init.user_groups.map((item, key) => (
                            <li key={key}>
                              {item.label}
                              <button className="btn btn-icon" onClick={() => handelRemove('user_groups', item)}>
                                <img src="/images/x-circle.svg" alt="" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                  <span className="form-error">
                    {errors?.submitters_settings?.user_groups && errors.submitters_settings?.user_groups[0]}
                  </span>
                </div>

                <div className={`form-item form-item-select mb-4`}>
                  <label className="form-label form-label-big">Individual Users</label>
                  <div className="select-multy-wrapper">
                    <span className="select-placeholder">Choose one or several individual_users</span>
                    <MultiSelect
                      isDisabled={!recipient_settings_init.individual_users?.length && !individualUsersOptions.length}
                      options={individualUsersOptions}
                      value={recipient_settings_init.individual_users}
                      onChange={(e) => handelSettings(e, 'individual_users')}
                      placeholder=""
                      allName="All individual users"
                    />
                    {recipient_settings_init.individual_users?.length > 0 && (
                      <>
                        <ul className={'individual-users-list'}>
                          {recipient_settings_init.individual_users.map((item, key) => (
                            <li key={key}>
                              {item.label}
                              <button className="btn btn-icon" onClick={() => handelRemove('individual_users', item)}>
                                <img src="/images/x-circle.svg" alt="" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    <span className="form-error">
                      {errors?.submitters_settings?.individual_users && errors.submitters_settings?.individual_users[0]}
                    </span>
                  </div>
                </div>
              </>
            </div>
          </div>
        )}
        {formData.submission_settings.has_strict_order_of_submissions && hasStepsToFill && (
          <div className="form-control-item-settings">
            <div className="form-settings-label">
              <label className="form-label form-label-big">Specify strict order of submitters (required)</label>
              <span>it is necessary to provide a clear and definite sequence for the form submitting </span>
            </div>
            <div className="form-settings-inputs">
              <StrictOrderSteps
                steps={recipient_settings_init.submitters_order ?? []}
                options={stepSubmitterOptions}
                splitByEntityType
                withHireMonth={(stepIndex) => canInitiatorFilterByHireDate(formData.submission_settings, stepIndex)}
                allowMultipleSubmitters={hasMultipleSubmissionsStrictOrder}
                lockedFinalSubmitters={lockedFinalSubmitters}
                lockedStepSubmitters={creatorLockedSubmitters}
                stepNames={creatorSteps?.map(getStepName)}
                hideLockedSteps
                isStepCountFixed={!!creatorSteps}
                isNotificationReadOnly={!!creatorSteps}
                hideNotifications={hideStepNotifications}
                onChangeStepSubmitters={handleChangeStepSubmitters}
                onChangeStep={handleChangeStep}
                onAddStep={addNewOrder}
                onRemoveStep={orderRemove}
                getStepErrors={getStepErrors}
                renderStepErrors={renderStepErrors}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
